import React, { useEffect, useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'

import {
  WHOLESALE_DELIVERY_TIERS,
  calculateMarginComparison,
  getRecommendedDeliveryTierId,
  getRecommendedWholesaleItemIndexes,
  getWholesaleDeliveryTier,
} from '../../utils/marginComparison'
import styles from './MarginComparisonDialog.module.css'

const money = (cents) => cents === null || cents === undefined
  ? 'Not found'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)

const CostMath = ({ unitCostCents, quantity }) => {
  if (unitCostCents === null) return <span className={styles.missing}>Product not found</span>
  return (
    <div className={styles.costMath}>
      <span>{money(unitCostCents)} × {quantity}</span>
      <strong>{money(Math.round(unitCostCents * quantity))}</strong>
    </div>
  )
}

const MarginComparisonDialog = ({ open, comparison, applying, saving, onCancel, onApply, onSaveWithoutChanges }) => {
  const busy = applying || saving
  const [selectedTierId, setSelectedTierId] = useState('')
  const [selectedItemIndexes, setSelectedItemIndexes] = useState([])

  useEffect(() => {
    const recommendedIndexes = getRecommendedWholesaleItemIndexes(comparison)
    setSelectedItemIndexes(recommendedIndexes)
    setSelectedTierId(getRecommendedDeliveryTierId(comparison, recommendedIndexes))
  }, [comparison])

  const estimate = useMemo(
    () => calculateMarginComparison(comparison, selectedTierId, selectedItemIndexes),
    [comparison, selectedItemIndexes, selectedTierId]
  )
  const selectedTier = getWholesaleDeliveryTier(estimate.tierId)
  const recommendedTierId = getRecommendedDeliveryTierId(comparison, selectedItemIndexes)
  const availableTiers = recommendedTierId === 'no_delivery'
    ? WHOLESALE_DELIVERY_TIERS.filter((tier) => tier.id === 'no_delivery')
    : WHOLESALE_DELIVERY_TIERS.filter((tier) => tier.id !== 'no_delivery')
  const orderedTiers = [
    ...availableTiers.filter((tier) => tier.id === recommendedTierId),
    ...availableTiers.filter((tier) => tier.id !== recommendedTierId),
  ]

  const toggleItem = (itemIndex) => {
    const nextIndexes = selectedItemIndexes.some((value) => String(value) === String(itemIndex))
      ? selectedItemIndexes.filter((value) => String(value) !== String(itemIndex))
      : [...selectedItemIndexes, itemIndex]
    const nextRecommendedTierId = getRecommendedDeliveryTierId(comparison, nextIndexes)
    setSelectedItemIndexes(nextIndexes)
    setSelectedTierId((currentTierId) => (
      currentTierId === 'no_delivery' || nextRecommendedTierId === 'no_delivery'
        ? nextRecommendedTierId
        : currentTierId
    ))
  }

  const deliveryExplanation = estimate.deliveryMode === 'additional_only'
    ? `At least one orderable item remains at its PM cost, so the ${money(selectedTier.firstUnitCents)} first charge is omitted. ${estimate.wholesaleDeliveryUnitCount} selected Wholesale unit(s) × ${money(selectedTier.additionalUnitCents)}.`
    : estimate.deliveryMode === 'full_order'
      ? `Every orderable item is selected for Wholesale pricing: ${money(selectedTier.firstUnitCents)} for the first unit + ${Math.max(0, estimate.wholesaleDeliveryUnitCount - 1)} additional unit(s) × ${money(selectedTier.additionalUnitCents)}.`
      : estimate.wholesaleSourceLineCount > 0
        ? 'The selected Wholesale items are accessories, so no per-unit vendor delivery is included.'
        : 'Select at least one available Wholesale cost to calculate its vendor delivery.'

  const selectionSummary = estimate.wholesaleSourceLineCount === 0
    ? 'No Wholesale costs selected'
    : estimate.hasWholesaleSavings
      ? `Selected plan saves ${money(estimate.savingsCents)}`
      : `Selected plan costs ${money(estimate.pmAdvantageCents)} more than PM`

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="lg" fullWidth aria-labelledby="margin-comparison-title">
      <DialogTitle id="margin-comparison-title">Choose Wholesale costs to apply</DialogTitle>
      <DialogContent dividers>
        <p className={styles.intro}>
          Check only the lines you want changed. Applying updates this invoice&apos;s unit costs and internal delivery cost;
          it does not update either product catalog or the delivery fee charged to your customer.
        </p>

        <div className={`${styles.recommendation} ${estimate.hasWholesaleSavings ? styles.warning : styles.good}`}>
          <span>Selected purchasing plan</span>
          <strong>{selectionSummary}</strong>
        </div>

        <div className={styles.orderDelivery}>
          <div>
            <label htmlFor="wholesale-order-delivery">Wholesale delivery for this order</label>
            <Select
              id="wholesale-order-delivery"
              value={estimate.tierId}
              disabled={busy}
              onChange={(event) => setSelectedTierId(event.target.value)}
              variant="outlined"
              className={styles.tierSelect}
              inputProps={{ 'aria-label': 'Wholesale delivery tier for this order' }}
            >
              {orderedTiers.map((tier) => (
                <MenuItem value={tier.id} key={tier.id}>
                  {tier.label}{tier.id === recommendedTierId ? ' — Recommended' : ''}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={styles.deliveryMath}>
            <span>{deliveryExplanation}</span>
            <strong>Invoice delivery cost to apply: {money(estimate.wholesaleDeliveryCents)}</strong>
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <span>Line item</span><span>Qty</span><span>PM cost</span><span>Wholesale cost</span><span>Recommended</span><span>Apply</span>
          </div>
          {estimate.rows.map((row) => {
            const canSelect = !row.alreadySourced && row.wholesaleUnitCostCents !== null
            return (
              <div className={styles.tableRow} key={`${row.itemIndex}-${row.modelNumber}`}>
                <div className={styles.productCell} data-label="Line item">
                  <strong>{row.modelNumber}</strong>
                  <span>{row.otherProduct?.type || row.currentProduct?.type || 'Product type unavailable'}</span>
                  {row.alreadySourced ? <em>{row.sourceLabel} — excluded from purchasing plan</em> : null}
                  {row.selectedForWholesale && !row.isDeliveryUnit
                    ? <em>Accessory — no delivery unit counted</em>
                    : null}
                </div>
                <div data-label="Qty">{row.quantity}</div>
                <div data-label="PM cost">
                  <CostMath unitCostCents={row.pmUnitCostCents} quantity={row.quantity} />
                </div>
                <div data-label="Wholesale cost">
                  <CostMath unitCostCents={row.wholesaleUnitCostCents} quantity={row.quantity} />
                </div>
                <div data-label="Recommended">
                  {row.recommendedSource === 'existing' ? (
                    <span className={styles.muted}>Already sourced</span>
                  ) : row.recommendedSource === 'wholesale' ? (
                    <span className={styles.savings}>Wholesale</span>
                  ) : row.recommendedSource === 'pm' ? (
                    <span className={styles.currentWins}>PM</span>
                  ) : (
                    <span className={styles.missing}>PM cost unavailable</span>
                  )}
                </div>
                <div data-label="Apply Wholesale cost" className={styles.checkboxCell}>
                  <Checkbox
                    checked={row.selectedForWholesale}
                    disabled={!canSelect || busy}
                    color="primary"
                    inputProps={{ 'aria-label': `Apply Wholesale cost for ${row.modelNumber}` }}
                    onChange={() => toggleItem(row.itemIndex)}
                  />
                </div>
              </div>
            )
          })}
        </div>

        <div className={styles.totals}>
          <div><span>Current PM invoice costs</span><strong>{money(estimate.pmOrderTotalCents)}</strong></div>
          <div>
            <span>Selected merchandise costs</span><strong>{money(estimate.recommendedMerchandiseCents)}</strong>
            <span>Delivery cost to apply</span><strong>{money(estimate.wholesaleDeliveryCents)}</strong>
            <span>Selected order cost</span><strong>{money(estimate.recommendedOrderTotalCents)}</strong>
          </div>
        </div>

        <p className={styles.disclaimer}>Save without changes keeps your current costs and saves the invoice now. After applying selected costs, review the invoice and use Save and Continue when ready.</p>
      </DialogContent>
      <DialogActions className={styles.actions} disableSpacing>
        <Button onClick={onCancel} disabled={busy}>Back to invoice</Button>
        <Button onClick={onSaveWithoutChanges} disabled={busy} color="primary" variant="outlined">
          {saving ? 'Saving…' : 'Save without changes'}
        </Button>
        <Button
          onClick={() => onApply(estimate)}
          disabled={busy || estimate.wholesaleSourceLineCount === 0}
          color="primary"
          variant="contained"
        >
          {applying ? 'Applying…' : 'Apply selected costs to invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MarginComparisonDialog
